// Partes do site público que a equipa liga e desliga no backoffice (área "Site", guardado na definição
// "siteSections"). Este módulo é a ÚNICA fonte de: lista de partes, valores por omissão e que ficheiros
// pertencem a cada parte. Sem dependências: importável no browser (site, backoffice) e no backend (static.ts).
//
// Valores por omissão = o que vale enquanto nada estiver guardado E o que o HTML pré-renderizado mostra
// (o browser aplica depois os valores reais vindos de /v1/public/info).
//   comic — desligada: a BD ainda não está afinada (poses trocadas, personagens que mudam de idade entre
//           vinhetas); decisão do Alexandre 2026-10-08. Nada foi apagado: ligar no backoffice.
//   legal — desligada: as páginas legais (termos, privacidade, cookies) são rascunhos com marcadores
//           {{COMPANY_NAME}}, {{NIF}}… por preencher (ainda não há empresa). Ligar só depois de os preencher
//           (docs/legais-e-requisitos.md). O backend responde 404 às páginas enquanto estiver desligada.

export const SITE_SECTIONS = ["game", "cards", "world", "books", "comic", "downloads", "legal"] as const;
export type SiteSection = (typeof SITE_SECTIONS)[number];
export type SiteSections = Record<SiteSection, boolean>;

export const SITE_SECTIONS_DEFAULT: Readonly<SiteSections> = Object.freeze({
  game: true,
  cards: true,
  world: true,
  books: true,
  comic: false,
  downloads: true,
  legal: false,
});

/** Junta um valor guardado (pode faltar alguma parte, p. ex. uma acrescentada depois) com os valores por omissão. */
export function withSectionDefaults(v: Partial<Record<SiteSection, boolean>> | null | undefined): SiteSections {
  const out = { ...SITE_SECTIONS_DEFAULT };
  for (const k of SITE_SECTIONS) if (typeof v?.[k] === "boolean") out[k] = v[k];
  return out;
}

// --- Ficheiros de cada parte ---------------------------------------------------------------------------

export type DownloadKey = "bookPdf" | "bookEpub" | "compendium" | "comicPdf";

/** Ficheiros para descarregar (vivem em apps/site/public/downloads; copiados do jogo por tools/build_site_assets.py). */
export const SITE_DOWNLOADS: Record<"pt" | "en", Record<DownloadKey, string>> = {
  pt: {
    bookPdf: "/downloads/Alvorada.pdf",
    bookEpub: "/downloads/Alvorada.epub",
    compendium: "/downloads/Compendio-de-Vesper.pdf",
    comicPdf: "/downloads/Ecliptica_T1_V1_Cap01_PT.pdf",
  },
  en: {
    bookPdf: "/downloads/Dawnbreak.pdf",
    bookEpub: "/downloads/Dawnbreak.epub",
    compendium: "/downloads/Compendium-of-Vesper.pdf",
    comicPdf: "/downloads/Ecliptica_T1_V1_Cap01_EN.pdf",
  },
};

/** A que parte pertence cada ficheiro para descarregar (desligar a parte = o ficheiro deixa de se servir). */
export const DOWNLOAD_SECTION: Record<DownloadKey, SiteSection> = {
  bookPdf: "books",
  bookEpub: "books",
  compendium: "world",
  comicPdf: "comic",
};

const FILE_SECTION = new Map<string, SiteSection>(
  Object.values(SITE_DOWNLOADS).flatMap((files) => (Object.entries(files) as [DownloadKey, string][]).map(([k, path]) => [path, DOWNLOAD_SECTION[k]] as const)),
);

/**
 * Parte dona de um ficheiro do site (null = o ficheiro não depende de nenhuma parte e serve-se sempre).
 * Partes sem ficheiros próprios (cards, downloads) só escondem coisas no site: as imagens das cartas,
 * por exemplo, são usadas noutras páginas e no backoffice.
 */
export function sectionOfPath(path: string): SiteSection | null {
  // BD: páginas de amostra e qualquer PDF da BD (Ecliptica_T<tomo>_…), incluindo capítulos futuros.
  if (path.startsWith("/bd/") || /^\/downloads\/Ecliptica_T\d+_/.test(path)) return "comic";
  // Jogo para descarregar (Windows/macOS, servidos de GAME_DOWNLOADS_DIR; ver game-release.ts).
  if (path.startsWith("/game/")) return "game";
  // Imagens de referência das personagens do Mundo (as páginas /world/… são rotas do site, não ficheiros).
  if (path.startsWith("/world-art/")) return "world";
  // A página da galeria (/cards, /pt/cartas). As imagens em /cards/<língua>/… NÃO dependem da parte (ver acima).
  if (/^\/(pt\/cartas|cards)\/?$/.test(path)) return "cards";
  return FILE_SECTION.get(path) ?? null;
}

/** Ficheiros do site que só servem uma parte desligada: o backend responde 404 a estes caminhos. */
export function hiddenSitePath(path: string, sections: SiteSections = SITE_SECTIONS_DEFAULT): boolean {
  const s = sectionOfPath(path);
  return s !== null && !sections[s];
}
